import React, { useState, useRef, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { combineClassNames } from 'core/css';
import SvgIcon from 'core/icons/SvgIcon';
import { Translate } from 'core/translate';
import Spinner from '../../../common/spinner/Spinner';
import { CLASSNAME_UPDATING } from '../../../constants';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import LazyImageContext from '../../../common/LazyImageContext';
import { CLEAR_OPTION_TOGGLED } from '../../../store/actionTypes';
import FallbackCgi from '../../../common/cgi/FallbackCgi';
import useImage from '../../../common/images/useImage';
import useLargeBreakpoint from '../../../hooks/useLargeBreakpoint';

export const OptionCardPrimarySection = (props) => {
  const { children, className } = props;
  return (
    <div className={combineClassNames(['option-card__primary', className])}>
      {children}
    </div>
  );
};

export const OptionCard = ({
  option,
  type,
  className,
  onChange,
  children,
  image,
  selected,
  onKeyDown,
  role,
  optionMarker,
  disabled,
  isUpdating,
  isUpdatingOption,
  analyticsKey,
  analyticsKey2,
  optionToggled,
  dispatch,
  displayOnly,
  stacked = true,
}) => {
  const CHECKBOX = 'checkbox';
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const { hasError } = useImage(image);
  const { afterLoad, loadedImages, scrollPosition } = useContext(
    LazyImageContext
  );
  const isAtLeastLarge = useLargeBreakpoint();

  const optionCardClassName = combineClassNames(
    disabled || displayOnly ? 'card-container--disabled' : 'card-container',
    'stat-wrapper',
    className,
    !disabled && selected ? 'card-container--checked' : '',
    role === 'radio' && isUpdating && !isUpdatingOption && CLASSNAME_UPDATING,
    stacked
      ? 'card-container--column'
      : isAtLeastLarge
      ? 'card-container--row'
      : 'card-container--column'
  );

  const isUpdatedSelectedOption =
    optionToggled &&
    optionToggled.code === option.code &&
    !optionToggled.selected;

  const onClick = (event) => {
    if (disabled || displayOnly) {
      return;
    }
    onChange(event);
    setHovered(false);
  };

  const onMouseEnter = () => {
    setHovered(true);
  };

  const onMouseLeave = () => {
    setHovered(false);
  };

  useEffect(() => {
    if (displayOnly || disabled) {
      return;
    }

    const cardElement = cardRef.current;

    cardElement.addEventListener('mouseenter', onMouseEnter);
    cardElement.addEventListener('mouseleave', onMouseLeave);
    return () => {
      cardElement.removeEventListener('mouseenter', onMouseEnter);
      cardElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  const getOptionIndicator = () => {
    if (disabled) {
      return null;
    }

    if (isUpdatingOption) {
      return (
        <div className="option-indicator">
          <Spinner className="svg-icon--option" />
          {role === CHECKBOX && (
            <div className="option-indicator__label">
              {selected ? (
                <Translate value="component.options.removing" />
              ) : (
                <Translate value="component.options.adding" />
              )}
            </div>
          )}
        </div>
      );
    }

    if (hovered && !isUpdatedSelectedOption) {
      return selected ? (
        <div className="option-indicator">
          <SvgIcon name="remove" className="svg-icon--option" />
          {role === CHECKBOX ? (
            <div className="option-indicator__label">
              <Translate value="component.options.remove" />
            </div>
          ) : null}
        </div>
      ) : role === CHECKBOX ? (
        <div className="option-indicator">
          <SvgIcon name="add" className="svg-icon--option" />
          <div className="option-indicator__label">
            <Translate value="component.options.add" />
          </div>
        </div>
      ) : null;
    }

    if (selected) {
      if (isUpdatedSelectedOption) {
        setTimeout(
          () =>
            dispatch({
              type: CLEAR_OPTION_TOGGLED,
            }),
          2000
        );
      }

      return (
        <div
          className={combineClassNames(
            'option-indicator',
            isUpdatedSelectedOption ? 'option-indicator--animate' : ''
          )}>
          <SvgIcon name="check" className="svg-icon--option" />
        </div>
      );
    }

    return role === CHECKBOX ? (
      <div className="option-indicator">
        <SvgIcon name="add" className="svg-icon--option" />
      </div>
    ) : null;
  };

  const imageAlt = option.description || option.code;

  return (
    <div
      role={role}
      tabIndex="0"
      aria-checked={selected}
      onClick={onClick}
      className={optionCardClassName}
      onKeyDown={onKeyDown}
      ref={cardRef}
      data-dtm={analyticsKey}
      data-dtm2={analyticsKey2}>
      <div className="option-card__image-container">
        {image && !hasError ? (
          <LazyLoadImage
            className={combineClassNames([
              'option-card__image',
              role === CHECKBOX && hovered ? 'option-card__image--hover' : '',
              (isUpdating && role === 'radio') || isUpdatingOption
                ? CLASSNAME_UPDATING
                : '',
              loadedImages && !loadedImages.get(image)
                ? 'option-card__image--fadeIn'
                : '',
            ])}
            scrollPosition={scrollPosition}
            src={image}
            visibleByDefault={loadedImages && loadedImages.get(image)}
            afterLoad={afterLoad ? () => afterLoad(image) : null}
            alt={imageAlt}
          />
        ) : (
          <FallbackCgi fullWidth hideLabel noPadding />
        )}
        {optionMarker ? (
          <div
            className={combineClassNames([
              'option-card__marker',
              `option-card__marker--${type}`,
            ])}>
            {optionMarker}
          </div>
        ) : null}
        {getOptionIndicator()}
      </div>
      {children}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    optionToggled: state.optionToggled,
  };
};

export default connect(mapStateToProps)(OptionCard);
